import React, { useEffect, useState } from "react";
import {
    Button,
    Form,
    Table,
    Spinner,
    Modal,
    Container,
    Row,
    Col,
    InputGroup,
    ListGroup,
    Card,
    Badge
} from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { dotoniwoofApi } from '../api/dotoniwooApi';
import { useAuth } from '../context/AuthContext';
import { Helmet } from 'react-helmet';
import NavContainer from './Nav';
import OffCanvasNav from './OffCanvasNav';
import Titulo from '../elements/Titulo';
import AlertModal from './AlertModal';
import useAlertModal from '../hooks/useAlertModal';
import './ModernPOS.css';

const ModernPOS = () => {
    const { user } = useAuth();
    const [searchTerm, setSearchTerm] = useState('');
    const [items, setItems] = useState([]);
    const [cart, setCart] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [editedPrice, setEditedPrice] = useState('');
    const [editedQuantity, setEditedQuantity] = useState(1);
    const [total, setTotal] = useState(0);
    const [subtotal, setSubtotal] = useState(0);
    const [tax, setTax] = useState(0);
    const { showAlert, showAlertModal, alertMessage, alertType, hideAlert } = useAlertModal();
    const [customerName, setCustomerName] = useState('');
    const [customerPhone, setCustomerPhone] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('efectivo');
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    
    const formatter = new Intl.NumberFormat('es-CO', {
        style: 'currency',
        currency: 'COP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    });

    const taxRate = 0.19; // 19% IVA

    useEffect(() => {
        getItems();
    }, []);

    useEffect(() => {
        const newSubtotal = cart.reduce((acc, item) => acc + (item.price * item.quantity), 0);
        const newTax = newSubtotal * taxRate;
        const newTotal = newSubtotal + newTax;
        
        setSubtotal(newSubtotal);
        setTax(newTax);
        setTotal(newTotal);
    }, [cart]);

    const getItems = async () => {
        setLoading(true);
        try {
            const res = await dotoniwoofApi.get(`/item.php`);
            const responseApi = res.data;
            if (responseApi.code === 200) {
                setItems(responseApi.message);
            }
        } catch (err) {
            console.error('Error al cargar productos:', err);
        } finally {
            setLoading(false);
        }
    };

    const filteredItems = items.filter((item) =>
        item.product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.product.sku?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const addToCart = (item) => {
        const existingItem = cart.find(cartItem => cartItem.id_item === item.id_item);
        
        if (existingItem) {
            setCart(cart.map(cartItem =>
                cartItem.id_item === item.id_item
                    ? { ...cartItem, quantity: cartItem.quantity + 1 }
                    : cartItem
            ));
        } else {
            setCart([...cart, { ...item, quantity: 1 }]);
        }
    };

    const removeFromCart = (id) => {
        setCart(cart.filter((item) => item.id_item !== id));
    };

    const updateQuantity = (id, newQuantity) => {
        if (newQuantity <= 0) {
            removeFromCart(id);
            return;
        }
        
        setCart(cart.map(item =>
            item.id_item === id ? { ...item, quantity: newQuantity } : item
        ));
    };

    const openEditModal = (product) => {
        setEditingProduct(product);
        setEditedPrice(product.price);
        setEditedQuantity(product.quantity);
        setShowModal(true);
    };

    const saveEdit = () => {
        setCart(cart.map((item) =>
            item.id_item === editingProduct.id_item
                ? { ...item, price: parseFloat(editedPrice), quantity: parseInt(editedQuantity) }
                : item
        ));
        setShowModal(false);
    };

    const clearCart = () => {
        setCart([]);
        setCustomerName('');
        setCustomerPhone('');
        setPaymentMethod('efectivo');
    };

    const processSale = async () => {
        if (cart.length === 0) return;
        
        setLoading(true);
        try {
            // Aquí iría la lógica para procesar la venta
            // Por ahora simulamos el proceso
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            showAlert('¡Venta procesada exitosamente!', 'success');
            clearCart();
        } catch (error) {
            console.error('Error al procesar la venta:', error);
        } finally {
            setLoading(false);
        }
    };

    const generatePDF = () => {
        const doc = new jsPDF({ unit: 'mm', format: [80, 150] });
        doc.setFont('helvetica');
        
        // Header
        doc.setFontSize(16);
        doc.text('DOTONIWOOF', 40, 10, { align: 'center' });
        doc.setFontSize(10);
        doc.text('Sistema POS', 40, 16, { align: 'center' });
        doc.text(`Fecha: ${new Date().toLocaleDateString('es-CO')}`, 40, 22, { align: 'center' });
        
        if (customerName) {
            doc.text(`Cliente: ${customerName}`, 40, 28, { align: 'center' });
        }
        
        // Items
        const rows = cart.map((item) => [
            item.product.name.substring(0, 15),
            item.quantity.toString(),
            formatter.format(item.price * item.quantity)
        ]);
        
        autoTable(doc, {
            startY: 35,
            head: [['Producto', 'Cant', 'Total']],
            body: rows,
            styles: { fontSize: 8 },
            headStyles: { fillColor: [234, 157, 14] },
        });
        
        // Totals
        const finalY = doc.lastAutoTable.finalY + 5;
        doc.text(`Subtotal: ${formatter.format(subtotal)}`, 10, finalY);
        doc.text(`IVA (19%): ${formatter.format(tax)}`, 10, finalY + 5);
        doc.setFontSize(12);
        doc.text(`TOTAL: ${formatter.format(total)}`, 10, finalY + 10);
        
        const pdfUrl = doc.output('bloburl');
        window.open(pdfUrl, '_blank');
    };

    return (
        <>
            <Helmet>
                <title>POS Moderno - DotoniWoof</title>
            </Helmet>
            <NavContainer />
            <OffCanvasNav />
            <Container fluid className="modern-pos-container">
                <Row className="h-100">
                    {/* Panel de Productos */}
                    <Col lg={8} className="products-panel">
                        <Card className="h-100 modern-card">
                            <Card.Header className="modern-card-header">
                                <div className="d-flex justify-content-between align-items-center">
                                    <h4 className="mb-0">
                                        <i className="fas fa-store me-2"></i>
                                        Productos Disponibles
                                    </h4>
                                    <Badge bg="primary" className="fs-6">
                                        {filteredItems.length} productos
                                    </Badge>
                                </div>
                            </Card.Header>
                            <Card.Body className="p-0">
                                {/* Barra de Búsqueda */}
                                <div className="search-section p-3 border-bottom">
                                    <InputGroup size="lg">
                                        <InputGroup.Text>
                                            <i className="fas fa-search"></i>
                                        </InputGroup.Text>
                                        <Form.Control
                                            type="text"
                                            placeholder="Buscar por nombre o SKU..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="modern-search-input"
                                            autoFocus
                                        />
                                    </InputGroup>
                                </div>

                                {/* Grid de Productos */}
                                <div className="products-grid">
                                    {loading ? (
                                        <div className="loading-container">
                                            <Spinner animation="border" variant="primary" />
                                            <p className="mt-2">Cargando productos...</p>
                                        </div>
                                    ) : (
                                        <Row className="g-2 p-3">
                                            {filteredItems.map((item) => (
                                                <Col xs={6} sm={4} md={3} lg={2} key={item.id_item}>
                                                    <motion.div
                                                        whileHover={{ scale: 1.02 }}
                                                        whileTap={{ scale: 0.98 }}
                                                    >
                                                        <Card 
                                                            className="product-card h-100"
                                                            onClick={() => addToCart(item)}
                                                        >
                                                            <Card.Body className="text-center p-2">
                                                                <div className="product-image mb-2">
                                                                    <i className="fas fa-box fa-2x text-muted"></i>
                                                                </div>
                                                                <h6 className="product-name mb-1">
                                                                    {item.product.name}
                                                                </h6>
                                                                <p className="product-price mb-0 text-success fw-bold">
                                                                    {formatter.format(item.price)}
                                                                </p>
                                                                {item.product.sku && (
                                                                    <small className="text-muted">
                                                                        SKU: {item.product.sku}
                                                                    </small>
                                                                )}
                                                            </Card.Body>
                                                        </Card>
                                                    </motion.div>
                                                </Col>
                                            ))}
                                        </Row>
                                    )}
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Panel del Carrito */}
                    <Col lg={4} className="cart-panel">
                        <Card className="h-100 modern-card">
                            <Card.Header className="modern-card-header">
                                <div className="d-flex justify-content-between align-items-center">
                                    <h4 className="mb-0">
                                        <i className="fas fa-shopping-cart me-2"></i>
                                        Carrito de Ventas
                                    </h4>
                                    <Badge bg="success" className="fs-6">
                                        {cart.length} items
                                    </Badge>
                                </div>
                            </Card.Header>
                            <Card.Body className="p-0">
                                {/* Lista del Carrito */}
                                <div className="cart-items">
                                    <AnimatePresence>
                                        {cart.length === 0 ? (
                                            <div className="empty-cart text-center p-4">
                                                <i className="fas fa-shopping-cart fa-3x text-muted mb-3"></i>
                                                <p className="text-muted">El carrito está vacío</p>
                                                <small className="text-muted">
                                                    Selecciona productos para comenzar
                                                </small>
                                            </div>
                                        ) : (
                                            <ListGroup variant="flush">
                                                {cart.map((item) => (
                                                    <motion.div
                                                        key={item.id_item}
                                                        initial={{ opacity: 0, x: 50 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        exit={{ opacity: 0, x: -50 }}
                                                        transition={{ duration: 0.3 }}
                                                    >
                                                        <ListGroup.Item className="cart-item">
                                                            <div className="d-flex justify-content-between align-items-center">
                                                                <div className="item-info">
                                                                    <h6 className="mb-1">{item.product.name}</h6>
                                                                    <small className="text-muted">
                                                                        {formatter.format(item.price)} c/u
                                                                    </small>
                                                                </div>
                                                                <div className="item-controls">
                                                                    <div className="quantity-controls mb-2">
                                                                        <Button
                                                                            variant="outline-secondary"
                                                                            size="sm"
                                                                            onClick={() => updateQuantity(item.id_item, item.quantity - 1)}
                                                                        >
                                                                            <i className="fas fa-minus"></i>
                                                                        </Button>
                                                                        <span className="mx-2 fw-bold">{item.quantity}</span>
                                                                        <Button
                                                                            variant="outline-secondary"
                                                                            size="sm"
                                                                            onClick={() => updateQuantity(item.id_item, item.quantity + 1)}
                                                                        >
                                                                            <i className="fas fa-plus"></i>
                                                                        </Button>
                                                                    </div>
                                                                    <div className="d-flex gap-1">
                                                                        <Button
                                                                            variant="outline-warning"
                                                                            size="sm"
                                                                            onClick={() => openEditModal(item)}
                                                                        >
                                                                            <i className="fas fa-edit"></i>
                                                                        </Button>
                                                                        <Button
                                                                            variant="outline-danger"
                                                                            size="sm"
                                                                            onClick={() => removeFromCart(item.id_item)}
                                                                        >
                                                                            <i className="fas fa-trash"></i>
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="item-total text-end">
                                                                <strong className="text-success">
                                                                    {formatter.format(item.price * item.quantity)}
                                                                </strong>
                                                            </div>
                                                        </ListGroup.Item>
                                                    </motion.div>
                                                ))}
                                            </ListGroup>
                                        )}
                                    </AnimatePresence>
                                </div>

                                {/* Totales */}
                                {cart.length > 0 && (
                                    <div className="totals-section p-3 border-top">
                                        <div className="total-row d-flex justify-content-between">
                                            <span>Subtotal:</span>
                                            <span>{formatter.format(subtotal)}</span>
                                        </div>
                                        <div className="total-row d-flex justify-content-between">
                                            <span>IVA (19%):</span>
                                            <span>{formatter.format(tax)}</span>
                                        </div>
                                        <hr />
                                        <div className="total-row d-flex justify-content-between fs-5 fw-bold">
                                            <span>TOTAL:</span>
                                            <span className="text-success">{formatter.format(total)}</span>
                                        </div>
                                    </div>
                                )}

                                {/* Información del Cliente */}
                                {cart.length > 0 && (
                                    <div className="customer-section p-3 border-top">
                                        <h6 className="mb-3">
                                            <i className="fas fa-user me-2"></i>
                                            Información del Cliente
                                        </h6>
                                        <Form.Group className="mb-2">
                                            <Form.Control
                                                type="text"
                                                placeholder="Nombre del cliente"
                                                value={customerName}
                                                onChange={(e) => setCustomerName(e.target.value)}
                                                size="sm"
                                            />
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Control
                                                type="tel"
                                                placeholder="Teléfono"
                                                value={customerPhone}
                                                onChange={(e) => setCustomerPhone(e.target.value)}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </div>
                                )}

                                {/* Botones de Acción */}
                                {cart.length > 0 && (
                                    <div className="action-buttons p-3 border-top">
                                        <Row className="g-2">
                                            <Col>
                                                <Button
                                                    variant="success"
                                                    className="w-100"
                                                    onClick={processSale}
                                                    disabled={loading}
                                                >
                                                    {loading ? (
                                                        <Spinner size="sm" className="me-2" />
                                                    ) : (
                                                        <i className="fas fa-credit-card me-2"></i>
                                                    )}
                                                    Procesar Venta
                                                </Button>
                                            </Col>
                                            <Col>
                                                <Button
                                                    variant="outline-primary"
                                                    className="w-100"
                                                    onClick={generatePDF}
                                                >
                                                    <i className="fas fa-file-pdf me-2"></i>
                                                    Imprimir
                                                </Button>
                                            </Col>
                                        </Row>
                                        <Button
                                            variant="outline-secondary"
                                            className="w-100 mt-2"
                                            onClick={clearCart}
                                        >
                                            <i className="fas fa-trash me-2"></i>
                                            Limpiar Carrito
                                        </Button>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Modal de Edición */}
                <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Editar Producto</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Precio</Form.Label>
                            <Form.Control
                                type="number"
                                value={editedPrice}
                                onChange={(e) => setEditedPrice(e.target.value)}
                                placeholder="Precio del producto"
                            />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>Cantidad</Form.Label>
                            <Form.Control
                                type="number"
                                min="1"
                                value={editedQuantity}
                                onChange={(e) => setEditedQuantity(e.target.value)}
                                placeholder="Cantidad"
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowModal(false)}>
                            Cancelar
                        </Button>
                        <Button variant="primary" onClick={saveEdit}>
                            Guardar Cambios
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Modal de Alerta Temporal */}
                <AlertModal
                    show={showAlertModal}
                    onHide={hideAlert}
                    message={alertMessage}
                    type={alertType}
                />
            </Container>
        </>
    );
};

export default ModernPOS;
